import React, { useState } from "react";
import axios from "axios";
import { DateTime } from "luxon";
import config from "../../config";

const CreateTermAutomat = () => {
  // Initialize start and end date with default values
  const currentDateTime = DateTime.now();
  const nextDate = currentDateTime.plus({ days: 1 });
  const defaultStartTime = nextDate.set({ hour: 8, minute: 0 });
  const defaultEndTime = nextDate.set({ hour: 17, minute: 0 });

  const [startDate, setStartDate] = useState(defaultStartTime.toFormat("yyyy-MM-dd'T'HH:mm"));
  const [endDate, setEndDate] = useState(defaultEndTime.toFormat("yyyy-MM-dd'T'HH:mm"));
  const [durationStep, setDurationStep] = useState(60);
  const [termsToBeGenerated, setTermsToBeGenerated] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    const start = DateTime.fromISO(startDate);
    const end = DateTime.fromISO(endDate);

    if (start > end) {
      setErrorMessage("Koniec termínov musí byť po začiatku termínov.");
      return;
    }

    try {
      const durationInMinutes = parseInt(durationStep);
      const numOfTerms = Math.floor(end.diff(start, 'minutes').minutes / durationInMinutes);

      const terms = [];
      let currentDateTime = start;

      for (let i = 0; i < numOfTerms; i++) {
        terms.push(currentDateTime);
        currentDateTime = currentDateTime.plus({ minutes: durationInMinutes });
        if (currentDateTime > end) {
          break;
        }
      }

      setTermsToBeGenerated(terms);
      setSuccessMessage(`Počet termínov, ktoré sa vytvoria: ${terms.length}`);
    } catch (error) {
      setErrorMessage(
        "Nepodarilo sa vygenerovať časové úseky."
      );
      console.error("Failed to map terms:", error);
    }
  };

  const handleConfirmation = async () => {
    try {
      const token = localStorage.getItem("token");
      const createTermPromises = termsToBeGenerated.map((term) => {
        const data = {
          date: term.toUTC().toISO(),
          isOccupied: false,
          duration: durationStep,
        };

        return axios.post(`${config.apiEndpoint}/api/Terms`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      });

      await Promise.all(createTermPromises);

      setTermsToBeGenerated([]);
      setSuccessMessage("Termíny úspešne vytvorené!");
    } catch (error) {
      setErrorMessage(
        "Nepodarilo sa, pravdepodobne sú v týchto časoch už definované iné termíny."
      );
      console.error("Failed to create terms:", error);
    }
  };

  const handleConfirmationDismiss = () => {
    setTermsToBeGenerated([]);
    setSuccessMessage("");
  };

  const handleStartDateChange = (e) => {
    const localDate = DateTime.fromISO(e.target.value);
    const now = DateTime.now();

    if (localDate < now) {
      setStartDate(now.toFormat("yyyy-MM-dd'T'HH:mm"));
    } else {
      setStartDate(localDate.toFormat("yyyy-MM-dd'T'HH:mm"));
    }

    const newEndDate = localDate.plus({ minutes: 480 });
    if (newEndDate > DateTime.fromISO(endDate)) {
      setEndDate(newEndDate.toFormat("yyyy-MM-dd'T'HH:mm"));
    }
  };

  const handleEndDateChange = (e) => {
    const localDate = DateTime.fromISO(e.target.value);
    const startDateObj = DateTime.fromISO(startDate);

    if (localDate <= startDateObj) {
      const newEndDate = startDateObj.plus({ minutes: 60 });
      setEndDate(newEndDate.toFormat("yyyy-MM-dd'T'HH:mm"));
    } else {
      setEndDate(localDate.toFormat("yyyy-MM-dd'T'HH:mm"));
    }
  };

  return (
    <div className="container-fluid py-3 px-0">
      <div className="d-flex align-items-center gap-2 mb-4">
        <i className="bi bi-calendar-plus fs-3 text-primary"></i>
        <h2 className="mb-0">Automatický generátor termínov</h2>
      </div>

      {successMessage && !termsToBeGenerated.length && (
        <div className="alert alert-success alert-dismissible shadow-sm d-flex align-items-center gap-2" role="alert">
          <i className="bi bi-check-circle-fill"></i>
          <div>{successMessage}</div>
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setSuccessMessage("")}></button>
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-danger alert-dismissible shadow-sm d-flex align-items-center gap-2" role="alert">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div>{errorMessage}</div>
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setErrorMessage("")}></button>
        </div>
      )}

      <div className="row">
        {/* FORM COLUMN */}
        <div className="col-12 col-xl-5 mb-4 mb-xl-0">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-header bg-white border-bottom py-3">
              <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                <i className="bi bi-sliders"></i>
                Parametre generovania
              </h5>
            </div>
            <div className="card-body p-4">
              <form onSubmit={handleSubmit} className="row g-4">
                <div className="col-12">
                  <label htmlFor="startDate" className="form-label fw-bold small text-muted text-uppercase">
                    Začiatok termínov
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-white"><i className="bi bi-clock"></i></span>
                    <input
                      type="datetime-local"
                      className="form-control form-control-lg"
                      id="startDate"
                      value={startDate}
                      min={DateTime.now().toFormat("yyyy-MM-dd'T'HH:mm")}
                      onChange={handleStartDateChange}
                      required
                    />
                  </div>
                </div>
                <div className="col-12">
                  <label htmlFor="endDate" className="form-label fw-bold small text-muted text-uppercase">
                    Koniec termínov
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-white"><i className="bi bi-clock-history"></i></span>
                    <input
                      type="datetime-local"
                      className="form-control form-control-lg"
                      id="endDate"
                      value={endDate}
                      min={startDate}
                      onChange={handleEndDateChange}
                      required
                      disabled={DateTime.fromISO(startDate) > DateTime.fromISO(endDate)}
                    />
                  </div>
                </div>
                <div className="col-12">
                  <label htmlFor="durationStep" className="form-label fw-bold small text-muted text-uppercase">
                    Dĺžka jedného termínu (Minúty)
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-white"><i className="bi bi-hourglass-split"></i></span>
                    <input
                      type="number"
                      className="form-control form-control-lg"
                      id="durationStep"
                      value={durationStep}
                      min="1"
                      onChange={(e) => setDurationStep(e.target.value === '' ? '' : parseInt(e.target.value))}
                      required
                    />
                  </div>
                </div>
                <div className="col-12 mt-4 pt-3 border-top">
                  <button type="submit" className="btn btn-primary btn-lg w-100 d-flex justify-content-center align-items-center gap-2">
                    <i className="bi bi-magic"></i>
                    Vygenerovať náhľad
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* PREVIEW COLUMN */}
        <div className="col-12 col-xl-7">
          {termsToBeGenerated.length > 0 ? (
            <div className="card shadow-sm border-0 border-top border-primary border-4 h-100">
              <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                  <i className="bi bi-eye"></i>
                  Zoznam na {termsToBeGenerated.length} termínov
                </h5>
                <button className="btn btn-outline-secondary btn-sm" onClick={handleConfirmationDismiss}>
                  Zrušiť
                </button>
              </div>
              <div className="card-body p-4 bg-light bg-opacity-50">
                <div className="d-flex flex-wrap gap-2 mb-4">
                  {termsToBeGenerated.map((term, index) => (
                    <span key={index} className="badge bg-white text-dark border p-2 shadow-sm d-flex align-items-center gap-2" style={{ fontSize: '0.9rem' }}>
                      <i className="bi bi-calendar-event text-primary"></i>
                      <span>
                        <strong className="me-1">{term.toFormat("HH:mm")}</strong>
                        <span className="text-muted small">{term.toFormat("dd.MM.")}</span>
                      </span>
                    </span>
                  ))}
                </div>
                <button className="btn btn-success btn-lg w-100 d-flex justify-content-center align-items-center gap-2" onClick={handleConfirmation}>
                  <i className="bi bi-database-add"></i>
                  Zápísať {termsToBeGenerated.length} termínov do databázy
                </button>
              </div>
            </div>
          ) : (
            <div className="card shadow-none border border-dashed border-2 h-100 bg-transparent text-center d-flex flex-column justify-content-center py-5">
              <div className="text-muted">
                <i className="bi bi-clock-history fs-1 mb-3 opacity-50"></i>
                <h5>Žiadny náhľad</h5>
                <p className="mb-0 mx-auto" style={{ maxWidth: "300px" }}>
                  Zadajte parametre vľavo a kliknite na "Vygenerovať náhľad" pre zobrazenie budúcich termínov.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateTermAutomat;
